import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@services/api';
import { useDomainStore } from '@stores/domainStore';
import { useSignalR } from '../hooks/useSignalR';
import { TalkingAvatar } from '@components/TalkingAvatar';
import {
  loadKoridorVoice, tapCallPlayback, UNAVAILABLE, END_MESSAGES,
  type KoridorCall, type KoridorCallState, type StartVoiceCallResponse,
} from '../services/koridorVoice';
import './VoiceCallPage.scss';

interface Bubble {
  id: number;
  role: 'user' | 'assistant';
  text: string;
  /** Assistant only: still being spoken. */
  live?: boolean;
  /** Assistant only: the guest spoke over it — the text is what they heard. */
  interrupted?: boolean;
}

export default function VoiceCallPage() {
  const navigate = useNavigate();
  const { getBasePath } = useDomainStore();

  const [callActive, setCallActive] = useState(false);
  const [starting, setStarting] = useState(false);
  const [phase, setPhase] = useState<KoridorCallState | null>(null);
  const [bubbles, setBubbles] = useState<Bubble[]>([]);
  const [partial, setPartial] = useState('');
  const [tool, setTool] = useState<string | null>(null);
  const [muted, setMuted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [callDuration, setCallDuration] = useState(0);
  // Showing the AI something: the photo goes to the chat like any photo and its result into the call.
  const [photosEnabled, setPhotosEnabled] = useState(false);
  const [photoAsk, setPhotoAsk] = useState<string | null>(null);
  const [sendingPhoto, setSendingPhoto] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const { on } = useSignalR();

  const callRef = useRef<KoridorCall | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const bubbleIdRef = useRef(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const avatarMountRef = useRef<HTMLDivElement>(null);
  const avatarRef = useRef<TalkingAvatar | null>(null);
  // The mouth follows the call audio itself; otherwise it mouths along to each sentence's text.
  const audioTappedRef = useRef(false);

  // The persona is on screen and alive (blinking, swaying) before the call starts.
  useEffect(() => {
    if (!avatarMountRef.current) return;
    const coat = getComputedStyle(document.documentElement).getPropertyValue('--theme-color-1').trim();
    const avatar = new TalkingAvatar(avatarMountRef.current, { colors: coat ? { coat } : {} });
    avatar.setMood('smile');
    avatarRef.current = avatar;
    return () => { avatar.destroy(); avatarRef.current = null; };
  }, []);

  // Fetch the client up front so the tap only has to ask for a ticket. Not the ticket itself: it is
  // single-use and expires in two minutes.
  useEffect(() => { loadKoridorVoice().catch(() => { /* retried on tap */ }); }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [bubbles, partial]);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Leaving the page hangs up.
  useEffect(() => () => {
    stopTimer();
    callRef.current?.hangup().catch(() => { /* page is gone */ });
    callRef.current = null;
  }, [stopTimer]);

  const finishCall = useCallback(() => {
    callRef.current = null;
    stopTimer();
    setCallActive(false);
    setPhase(null);
    setPartial('');
    setTool(null);
    setMuted(false);
    setPhotoAsk(null);
    avatarRef.current?.stop();
    avatarRef.current?.setMood('smile');
    audioTappedRef.current = false;
    // Anything still marked as being spoken is as finished as it will get.
    setBubbles(prev => prev.map(b => (b.live ? { ...b, live: false } : b)));
  }, [stopTimer]);

  const addBubble = (role: Bubble['role'], text: string) =>
    setBubbles(prev => [...prev, { id: ++bubbleIdRef.current, role, text }]);

  // The AI asked to see something (request_photo). Opening the camera needs a tap, so it is offered.
  useEffect(() => on('PhotoRequested', (data) => {
    if (!callRef.current) return;
    const reason = typeof data.reason === 'string' ? data.reason : '';
    setPhotoAsk(reason || 'something');
  }), [on]);

  const sendPhoto = useCallback(async (file: File) => {
    setSendingPhoto(true);
    setPhotoAsk(null);
    try {
      const { compressFile } = await import('../utils/compressMedia');
      const compressed = await compressFile(file);
      const form = new FormData();
      form.append('file', compressed);
      const { data: uploaded } = await apiClient.post<{ fileUrl: string }>('/api/v1/guest/messages/upload', form);
      await apiClient.post('/api/v1/guest/messages/send', {
        content: '',
        attachments: [{
          fileUrl: uploaded.fileUrl,
          fileName: file.name || 'photo.jpg',
          contentType: compressed.type || file.type || 'image/jpeg',
          fileSize: compressed.size,
        }],
      });
      addBubble('user', '📷 Photo sent');
    } catch (err) {
      console.error('Photo during call failed:', err);
      setError("The photo couldn't be sent. Please try again.");
    } finally {
      setSendingPhoto(false);
    }
  }, []);

  const onPhotoPicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) sendPhoto(file);
  };

  // Must run inside the tap: browsers (iOS Safari strictly) only start the microphone and audio from
  // a user gesture.
  const startCall = useCallback(async () => {
    if (starting || callRef.current) return;
    setStarting(true);
    setError(null);
    setBubbles([]);
    setPartial('');
    setCallDuration(0);

    try {
      const [{ data }, KoridorVoice] = await Promise.all([
        apiClient.post<StartVoiceCallResponse>('/api/v1/guest/messages/voice-call/start', { voice: 'nova' }),
        loadKoridorVoice(),
      ]);

      if (!data.success || !data.ticket || !data.socketUrl) {
        setError(data.error || UNAVAILABLE);
        return;
      }
      setPhotosEnabled(!!data.photosEnabled);

      const call = await KoridorVoice.start({
        ticket: data.ticket,
        socketUrl: data.socketUrl,
        onState: (state) => {
          setPhase(state);
          if (state !== 'thinking') setTool(null);
          const avatar = avatarRef.current;
          if (!avatar) return;
          if (state === 'thinking') avatar.setMood('thinking');
          else if (state === 'speaking') avatar.setMood('neutral');
          else {
            // Listening again — after the answer, or because the guest spoke over it: mouth shut now.
            avatar.setSpeaking(false);
            avatar.setMood('smile');
          }
        },
        onPartial: (text) => setPartial(text),
        onUserText: (text) => {
          setPartial('');
          if (text.trim()) addBubble('user', text);
        },
        // Each sentence as it starts playing joins the answer being spoken. Koridor's turnEnd usually
        // arrives while the audio is still playing, so the last sentences can start AFTER the bubble
        // already holds the whole answer — those are already on screen, not a new answer.
        onAssistantText: (text) => {
          // Fires as the sentence starts playing, so it is in time with the voice.
          if (audioTappedRef.current) avatarRef.current?.cueSentence(text);
          else avatarRef.current?.speakAlong(text);
          setBubbles(prev => {
            const last = prev[prev.length - 1];
            if (last?.role === 'assistant' && last.text.includes(text.trim())) return prev;
            if (last?.role === 'assistant' && last.live) {
              return [...prev.slice(0, -1), { ...last, text: `${last.text} ${text}`.trim() }];
            }
            return [...prev, { id: ++bubbleIdRef.current, role: 'assistant', text, live: true }];
          });
        },
        // The answer as heard: replaces the sentences, cut where the guest interrupted.
        onTurnEnd: ({ text, interrupted }) => setBubbles(prev => {
          const last = prev[prev.length - 1];
          const open = last?.role === 'assistant' && last.live;
          const rest = open ? prev.slice(0, -1) : prev;
          if (!text.trim()) return rest;
          return [...rest, { id: open ? last.id : ++bubbleIdRef.current, role: 'assistant', text, interrupted }];
        }),
        onTool: (name) => setTool(name),
        onError: (message) => setError(message),
        onEnded: (reason) => {
          finishCall();
          const message = END_MESSAGES[reason];
          if (message) setError(message);
        },
      });

      callRef.current = call;
      audioTappedRef.current = !!avatarRef.current && tapCallPlayback(call, avatarRef.current);
      setCallActive(true);
      timerRef.current = setInterval(() => setCallDuration(s => s + 1), 1000);
    } catch (err) {
      console.error('Voice call failed to start:', err);
      setError(UNAVAILABLE);
    } finally {
      setStarting(false);
    }
  }, [starting, finishCall]);

  const endCall = useCallback(async () => {
    const call = callRef.current;
    if (!call) return;
    stopTimer();
    setPhase('ended');
    try {
      // Resolves once Koridor has delivered the closing transcript (up to ~25s).
      await call.hangup();
    } catch (err) {
      console.error('Hangup failed:', err);
    } finally {
      finishCall();
    }
  }, [finishCall, stopTimer]);

  const toggleMute = useCallback(() => {
    const next = !muted;
    callRef.current?.setMuted(next);
    setMuted(next);
  }, [muted]);

  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const goBack = () => navigate(`${getBasePath()}/welcome`);

  const ending = callActive && phase === 'ended';

  return (
    <div className="voice-call-page">
      {/* Header */}
      <div className="vc-header">
        <button className="vc-back-btn" onClick={callActive ? endCall : goBack} disabled={ending} aria-label="Back">
          ← {callActive ? 'End' : 'Back'}
        </button>
        <div className="vc-header-center">
          <h2>🎙️ Voice Assistant</h2>
          {callActive && (
            <span className="vc-duration">{formatDuration(callDuration)}</span>
          )}
        </div>
        <div className="vc-header-right" />
      </div>

      {/* The assistant's face */}
      <div className={`vc-avatar${phase === 'speaking' ? ' vc-avatar-speaking' : ''}`}>
        <div ref={avatarMountRef} className="vc-avatar-face" />
      </div>

      {/* Conversation area */}
      <div className="vc-messages">
        {!callActive && !starting && bubbles.length === 0 && (
          <div className="vc-empty">
            <h3>Voice Assistant</h3>
            <p>Tap the button below to talk to our assistant. You can speak over it at any time.</p>
          </div>
        )}
        {bubbles.map(b => (
          <div key={b.id} className={`vc-bubble vc-bubble-${b.role}`}>
            <div className="vc-bubble-content">
              {b.text}
              {b.interrupted && <span className="vc-interrupted"> …</span>}
            </div>
          </div>
        ))}
        {partial && (
          <div className="vc-bubble vc-bubble-user vc-partial">
            <div className="vc-bubble-content">{partial}</div>
          </div>
        )}
        {(starting || (callActive && phase === 'connecting')) && (
          <div className="vc-status-indicator">
            <div className="vc-spinner" />
            <span>Connecting...</span>
          </div>
        )}
        {callActive && phase === 'listening' && !partial && (
          <div className="vc-status-indicator">
            <div className="vc-pulse" />
            <span>{muted ? 'Muted' : 'Listening...'}</span>
          </div>
        )}
        {callActive && phase === 'thinking' && (
          <div className="vc-status-indicator">
            <div className="vc-spinner" />
            <span>{tool ? 'Checking...' : 'Thinking...'}</span>
          </div>
        )}
        {callActive && phase === 'speaking' && (
          <div className="vc-status-indicator">
            <div className="vc-speaker-anim">
              <span /><span /><span /><span />
            </div>
            <span>Speaking...</span>
          </div>
        )}
        {sendingPhoto && (
          <div className="vc-status-indicator">
            <div className="vc-spinner" />
            <span>Sending photo...</span>
          </div>
        )}
        {ending && (
          <div className="vc-status-indicator">
            <div className="vc-spinner" />
            <span>Ending call...</span>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Error banner */}
      {error && (
        <div className="vc-error">
          {error}
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {/* The AI asked to see something */}
      {callActive && photosEnabled && photoAsk && !sendingPhoto && (
        <div className="vc-photo-ask">
          <span>📷 The assistant would like to see {photoAsk}.</span>
          <button onClick={() => photoInputRef.current?.click()}>Take photo</button>
          <button className="vc-photo-ask-dismiss" onClick={() => setPhotoAsk(null)} aria-label="Dismiss">✕</button>
        </div>
      )}
      <input ref={photoInputRef} type="file" accept="image/*" capture="environment" hidden onChange={onPhotoPicked} />

      {/* Bottom controls */}
      <div className="vc-controls">
        {!callActive ? (
          <button className="vc-start-btn" onClick={startCall} disabled={starting}>
            <span className="vc-mic-icon">🎙️</span>
            {starting ? 'Connecting...' : 'Start Call'}
          </button>
        ) : (
          <div className="vc-call-row">
            <button
              className={`vc-mute-btn${muted ? ' vc-mute-btn-on' : ''}`}
              onClick={toggleMute}
              disabled={ending}
              aria-pressed={muted}
            >
              {muted ? '🔇 Unmute' : '🎤 Mute'}
            </button>
            {photosEnabled && (
              <button
                className="vc-photo-btn"
                onClick={() => photoInputRef.current?.click()}
                disabled={ending || sendingPhoto}
              >
                📷 Show the AI
              </button>
            )}
            <button className="vc-end-btn" onClick={endCall} disabled={ending}>
              End Call
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
